import type { ChatMessage, ReactionKey } from './chat.types';

import {
  ArrowShareLeftIcon,
  IconButton,
  MenuIcon,
  SmileyIcon,
  Spinner,
  Tooltip,
  classNames,
  useOnClickOutside,
} from '@movingimage-evp/mi-ui-component-library';
import { format } from 'date-fns';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { reactionIcons } from './chat.types';
import { LinkifiedMessage } from './linkified-message';
import { useChat } from './use-sendbird-chat';
import { getLocale } from '../../i18n';

import styles from './message.module.css';

type MessageProps = {
  message: ChatMessage;
  currentUserId?: string;
  userIsModerator?: boolean;
  loading?: boolean;
  hideReplyCount?: boolean;
  testId?: string;
  readOnly?: boolean;
  anonymous?: boolean;
  onDeleteMessage?: (message: ChatMessage) => void;
  onEditMessage?: (message: ChatMessage) => void;
  onReactToMessage?: (messageId: ChatMessage['messageId'], reactionName: ReactionKey) => void;
  onReplyToMessage?: (messageId: ChatMessage['messageId']) => void;
};

export function Message({
  message,
  currentUserId,
  userIsModerator,
  loading,
  hideReplyCount,
  testId = 'chat-message',
  readOnly,
  anonymous,
  onDeleteMessage,
  onEditMessage,
  onReactToMessage,
  onReplyToMessage,
}: MessageProps) {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const { getReactionsByMessageId, getNumberOfReactionsByMessageId, hasUserReactedToMessage } = useChat();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [reactionMenuIsOpen, setReactionMenuIsOpen] = useState(false);

  const reactions = getReactionsByMessageId(message.messageId);
  const fullTestId = `${testId}-${message.messageId}`;
  const sentByCurrentUser = message.sender.userId === currentUserId;
  const sentByModerator = message.sender.role === 'operator';
  const replyCount = message.threadInfo?.replyCount || 0;
  const hasReplies = replyCount > 0;
  const menuAvailable = !readOnly && [onDeleteMessage, onEditMessage, onReactToMessage, onReplyToMessage].some(Boolean);
  const nickname = sentByModerator ? t('common.moderator') : anonymous ? '' : message.sender.nickname;

  const handleMenuState = () => {
    const updatedMenuIsOpen = !menuIsOpen;

    if (updatedMenuIsOpen && reactionMenuIsOpen) {
      setReactionMenuIsOpen(false);
    }

    setMenuIsOpen(updatedMenuIsOpen);
  };

  const handleReactionMenuState = () => {
    const updatedReactionMenuIsOpen = !reactionMenuIsOpen;

    if (updatedReactionMenuIsOpen && menuIsOpen) {
      setMenuIsOpen(false);
    }

    setReactionMenuIsOpen(updatedReactionMenuIsOpen);
  };

  const handleEditMessage = () => {
    onEditMessage?.(message);
    setMenuIsOpen(false);
  };

  const handleReplyButtonClick = () => {
    onReplyToMessage?.(message.messageId);
    setMenuIsOpen(false);
  };

  const handleOnReaction = (reactionName: ReactionKey) => {
    onReactToMessage?.(message.messageId, reactionName);
    setReactionMenuIsOpen(false);
  };

  useOnClickOutside(containerRef, () => {
    setMenuIsOpen(false);
    setReactionMenuIsOpen(false);
  });

  return (
    <div
      ref={containerRef}
      data-testid={fullTestId}
      className={classNames(
        styles.message,
        sentByCurrentUser && styles.sentByCurrentUser,
        sentByModerator && styles.sentByModerator,
        menuIsOpen && styles.menuIsOpen,
        reactionMenuIsOpen && styles.reactionMenuIsOpen
      )}
    >
      <div className={styles.sender}>
        <span data-testid={`${fullTestId}-sender`} className={styles.nickname}>
          {nickname}
        </span>

        <MessageDate data-testid={`${fullTestId}-date`} timestamp={message.createdAt || 0} />
      </div>

      <div className={styles.textAndButtonsWrapper}>
        <div data-testid={`${fullTestId}-text`} className={styles.text}>
          <LinkifiedMessage>{message.message}</LinkifiedMessage>
        </div>

        {menuAvailable && (
          <div className={styles.buttons}>
            {onReplyToMessage && (
              <Tooltip label={t('common.chat.replyButton')} hidden={reactionMenuIsOpen}>
                <IconButton
                  rounded
                  type="button"
                  data-testid={`${fullTestId}-reply-button`}
                  onClick={handleReplyButtonClick}
                  aria-label={t('common.chat.replyButton')}
                >
                  <ArrowShareLeftIcon />
                </IconButton>
              </Tooltip>
            )}

            {onReactToMessage && (
              <div className={styles.reactionsMenuWrapper}>
                <Tooltip label={t('common.chat.reactionButton')} hidden={reactionMenuIsOpen}>
                  <IconButton
                    rounded
                    type="button"
                    data-testid={`${fullTestId}-reactions-menu-button`}
                    aria-label={t('common.chat.reactionButton')}
                    className={styles.reactionsMenuButton}
                    onClick={handleReactionMenuState}
                  >
                    <SmileyIcon />
                  </IconButton>
                </Tooltip>

                <div className={styles.reactionsMenu} data-testid="reaction-menu">
                  {Object.entries(reactionIcons).map(([reactionName, icon]) => (
                    <button
                      key={reactionName}
                      type="button"
                      data-testid={`${fullTestId}-reactions-menu-${reactionName}-button`}
                      className={styles.reaction}
                      onMouseDown={() => handleOnReaction(reactionName as ReactionKey)}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(sentByCurrentUser || userIsModerator) && (
              <Tooltip label={t('common.chat.menuButton')} hidden={menuIsOpen}>
                <IconButton
                  rounded
                  type="button"
                  data-testid={`${fullTestId}-menu-button`}
                  aria-label={t('common.chat.menuButton')}
                  onClick={handleMenuState}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}

            {(sentByCurrentUser || userIsModerator) && (
              <div className={classNames(styles.menu, loading && styles.loading)} data-testid="menu">
                {loading && <Spinner />}

                {onEditMessage && (
                  <button type="button" data-testid={`${fullTestId}-edit-button`} onClick={handleEditMessage}>
                    {t('common.chat.editMessage')}
                  </button>
                )}

                {onDeleteMessage && !hasReplies && (
                  <button
                    type="button"
                    data-testid={`${fullTestId}-delete-button`}
                    onClick={() => onDeleteMessage(message)}
                  >
                    {t('common.chat.deleteMessage')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {reactions && (
        <div className={styles.reactions}>
          {Object.keys(reactions)
            .filter((key) => getNumberOfReactionsByMessageId(message.messageId, key as ReactionKey))
            .map((key) => (
              <button
                key={key}
                data-testid={`${fullTestId}-reaction-${key}-button`}
                type="button"
                className={classNames(
                  styles.reaction,
                  hasUserReactedToMessage(message.messageId, key as ReactionKey) && styles.fromCurrentUser
                )}
                onClick={() => onReactToMessage?.(message.messageId, key as ReactionKey)}
              >
                {reactionIcons[key as ReactionKey]}{' '}
                {getNumberOfReactionsByMessageId(message.messageId, key as ReactionKey)}
              </button>
            ))}
        </div>
      )}

      {!hideReplyCount && hasReplies && (
        <button
          type="button"
          data-testid={`${fullTestId}-reply-count`}
          className={styles.replyCount}
          onClick={handleReplyButtonClick}
        >
          {t('common.chat.replies.count', { count: replyCount })}
        </button>
      )}
    </div>
  );
}

export function MessageDate({ timestamp, ...props }: { timestamp: number }) {
  const locale = getLocale();

  const isPastDay = (timestamp: number) => {
    const date = new Date(timestamp);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return date < today;
  };

  return (
    <div className={styles.date} {...props}>
      {format(timestamp, isPastDay(timestamp) ? 'HH:mm, dd MMM' : 'HH:mm', { locale })}
    </div>
  );
}
